import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  RefreshCw,
  XCircle,
  Upload,
  Search,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import DashboardCard from '@/components/dashboard/DashboardCard'
import StructureSyncControl from '@/components/StructureSyncControl'
import type { SyncStatus, FreshnessTier } from '@/types/shared'

interface SyncStatusCardProps {
  syncStatus: SyncStatus
  /** The property's backend-served canonical path. */
  propertyBasePath: string
  dirtyRatesCount?: number
  /**
   * The :manage_pricing ability. Defaults to true, matching the Recompute button beside it: both
   * endpoints authorize server-side, so the card shows the action and lets the backend refuse.
   * Pass `false` from a surface that knows the viewer is read-only.
   */
  canSync?: boolean
}

function timeAgo(isoString: string | null): string {
  if (!isoString) return 'Never'
  const now = new Date()
  const then = new Date(isoString)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// Freshness tiers come from the server (SyncHealth) — no client-side threshold math.
const tierColors: Record<FreshnessTier, 'green' | 'yellow' | 'red'> = {
  fresh: 'green',
  stale: 'yellow',
  outdated: 'red',
  unknown: 'red',
}

const dotColors = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-rose-500',
}

interface StatusRowProps {
  icon: React.ElementType
  label: string
  time: string | null
  tier: FreshnessTier
  detail?: string
}

function StatusRow({ icon: Icon, label, time, tier, detail }: StatusRowProps) {
  const color = tierColors[tier]
  return (
    <div className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/30">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted/50">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">
          {timeAgo(time)}
          {detail && <span> · {detail}</span>}
        </p>
      </div>
      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', dotColors[color])} />
    </div>
  )
}

export default function SyncStatusCard({
  syncStatus,
  propertyBasePath,
  dirtyRatesCount,
  canSync = true,
}: SyncStatusCardProps) {
  const [computing, setComputing] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pollBookings, setPollBookings] = useState(false)
  const [pushRates, setPushRates] = useState(false)

  const handleRecompute = () => {
    setDialogOpen(false)
    router.post(`${propertyBasePath}/recompute-rates`, {
      poll: pollBookings ? 'true' : 'false',
      push: pushRates ? 'true' : 'false',
    }, {
      onStart: () => setComputing(true),
      onFinish: () => {
        setComputing(false)
        setPollBookings(false)
        setPushRates(false)
      },
    })
  }

  const freshness = syncStatus.health?.freshness
  const verificationColor = syncStatus.lastVerificationDriftCount === null
    ? tierColors[freshness?.verification ?? 'unknown']
    : syncStatus.lastVerificationDriftCount > 0 ? 'yellow' : 'green'

  // Two DIFFERENT actions, deliberately not merged. Recompute stays primary (it is the far
  // more frequent one); structure sync sits below it at secondary weight with a caption that
  // names the distinction — an operator whose room list is stale must be able to see at a
  // glance that Recompute is not the button they want. Pinned to the card's bottom edge by
  // DashboardCard's footer slot, so it lines up with its neighbours in the row.
  const actions = (
    <div className="space-y-2">
      <button
        type="button"
        disabled={computing}
        aria-busy={computing}
        onClick={() => setDialogOpen(true)}
        className={cn(
          'inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs font-medium text-primary transition-all',
          'hover:bg-primary/10 hover:border-primary/30',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:pointer-events-none'
        )}
      >
        <Zap className={cn('h-3.5 w-3.5', computing && 'animate-spin')} />
        {computing ? 'Computing...' : 'Recompute rates'}
      </button>

      {canSync && (
        <StructureSyncControl syncPath={`${propertyBasePath}/sync-from-eviivo`} variant="card" />
      )}

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        <span className="font-medium text-foreground">Recompute rates</span> recalculates prices.{' '}
        {canSync && (
          <>
            <span className="font-medium text-foreground">Sync structure</span> re-reads which rooms
            and rate plans your PMS has.
          </>
        )}
      </p>
    </div>
  )

  return (
    <>
      <DashboardCard
        title="Sync Status"
        description="PMS sync health"
        padded={false}
        bodyClassName="divide-y divide-border/30 p-2"
        footer={actions}
      >
        <StatusRow
            icon={RefreshCw}
            label="Last Structure Sync"
            time={syncStatus.lastStructureSync}
            tier={freshness?.structure ?? 'unknown'}
          />
          <StatusRow
            icon={Zap}
            label="Last Computation"
            time={syncStatus.lastComputation}
            tier={freshness?.compute ?? 'unknown'}
          />
          <StatusRow
            icon={Upload}
            label="Last Push"
            time={syncStatus.lastPush}
            tier={freshness?.push ?? 'unknown'}
            detail={syncStatus.lastPushCount !== null ? `pushed ${syncStatus.lastPushCount} rates` : undefined}
          />
          <div className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/30">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted/50">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">Last Verification</p>
              <p className="text-xs text-muted-foreground">
                {timeAgo(syncStatus.lastVerification)}
                {syncStatus.lastVerificationDriftCount !== null && (
                  <span>
                    {' '} · {syncStatus.lastVerificationDriftCount === 0
                      ? 'no drift'
                      : `${syncStatus.lastVerificationDriftCount} drifted`}
                  </span>
                )}
              </p>
            </div>
            <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', dotColors[verificationColor])} />
          </div>

          {dirtyRatesCount != null && dirtyRatesCount > 0 && (
            <div className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/30">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                <Upload className="h-4 w-4 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Unpushed Rates</p>
                <p className="text-xs text-muted-foreground">
                  {dirtyRatesCount} rate{dirtyRatesCount !== 1 ? 's' : ''} not yet synced to your PMS
                </p>
              </div>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />
            </div>
          )}

          {syncStatus.health?.errorMessage && (
            <div className="flex items-center gap-3 rounded-lg p-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50">
                <XCircle className="h-4 w-4 text-rose-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-rose-600">Needs attention</p>
                <p className="text-xs text-rose-500">{syncStatus.health.errorMessage}</p>
              </div>
            </div>
          )}
      </DashboardCard>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Recompute Rates</DialogTitle>
            <DialogDescription>
              Run the rate engine to recalculate prices based on current rules and demand signals.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border/60 p-3 transition-colors hover:bg-muted/30">
              <input
                type="checkbox"
                checked={pollBookings}
                onChange={(e) => setPollBookings(e.target.checked)}
                className="mt-0.5 rounded accent-primary cursor-pointer"
              />
              <div>
                <p className="text-sm font-medium text-foreground">Fetch latest booking data from PMS</p>
                <p className="text-xs text-muted-foreground">Polls your PMS for recent booking changes before computing</p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border/60 p-3 transition-colors hover:bg-muted/30">
              <input
                type="checkbox"
                checked={pushRates}
                onChange={(e) => setPushRates(e.target.checked)}
                className="mt-0.5 rounded accent-primary cursor-pointer"
              />
              <div>
                <p className="text-sm font-medium text-foreground">Push computed rates to PMS</p>
                <p className="text-xs text-muted-foreground">Sends updated rates back to your PMS after computation</p>
              </div>
            </label>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">
              Cancel
            </Button>
            <Button onClick={handleRecompute} className="rounded-lg">
              <Zap className="mr-1.5 h-3.5 w-3.5" />
              Recompute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
